import { derivePriceProps } from '@dupe/common/analyticsCommerce'
import { decodeHTML } from 'entities'
import type { ChatEntrySource } from '~/src/lib/analytics/chatEntrySource'
import type { DupeSearchType } from '~/src/lib/analytics/googleAdsConversions'
import { isPromptUrl } from '~/src/lib/isPromptUrl'

/**
 * The search-outcome half of the chat-v2 `Performed Dupe` payload.
 *
 * Extracted from `BrainChat.tsx` so it can be tested: that file is ~7k lines
 * and the emitter sits inside a streaming handler, so a render test would prove
 * nothing about the values. Everything decision-shaped lives here.
 *
 * This emitter is 70.8% of `Performed Dupe` (7 days to 2026-08-19) — the
 * results page it was first built for is 0.07% — so these properties only reach
 * useful volume here. See docs/references/lifecycle-event-contract.md.
 */
export interface PerformedDupeUserMessage {
  content?: string | null
  /**
   * True when this message is the shopper answering the brain's clarifying
   * question (the message carries `answeredQuestion`). Both answer paths set
   * it: picking an option, and the carousel's "No preference" dismissal.
   */
  isClarificationAnswer?: boolean
  /** Images the shopper attached to this message, if any. */
  imageUrls?: string[] | null
}

export interface PerformedDupeChatInput {
  /** Every user message in the thread, oldest first, including the one that
   *  produced these products. */
  userMessages: PerformedDupeUserMessage[]
  /** Title frozen at the first resolved one after mount. Used ONLY as the URL
   *  paste fallback; see the note in `buildPerformedDupeChatProps`. */
  broadcastTitle: string | null | undefined
  /** The brain's source product for this turn, whatever shape it arrived in. */
  originalProduct: Record<string, unknown> | null | undefined
  /** Products the turn resolved to. The emitter only fires when this is
   *  non-empty — see the `zero_results` note below. */
  productCount: number
  /** Where the THREAD was started, frozen on mount — the URL that carried it
   *  is replaced before either event fires. See `chatEntrySource.ts`. */
  entrySource: ChatEntrySource
}

export interface PerformedDupeChatProps {
  q: string | null
  price_anchor: number | null
  price_anchor_currency: string | null
  results_count: number
  results_phase: 'full'
  query_type: PerformedDupeQueryType
  search_source: ChatEntrySource
}

/**
 * How the shopper started the search, in the words lifecycle uses: a pasted
 * product link, typed words, or a photo. Customer.io journeys branch on it.
 */
export type PerformedDupeQueryType = 'url' | 'text' | 'image'

export const QUERY_TYPE: Record<DupeSearchType, PerformedDupeQueryType> = {
  url_prefix: 'url',
  text: 'text',
  image: 'image',
}

/**
 * The message that carried the shopper's intent, skipping clarification
 * answers. Shared by the query text and the search-type derivation so the two
 * can never disagree about which message the search actually was.
 */
export const findIntentText = (
  userMessages: PerformedDupeUserMessage[]
): string | undefined =>
  [...userMessages]
    .reverse()
    .find((m) => !m.isClarificationAnswer && m.content?.trim())
    ?.content?.trim()

/**
 * Which input drove this search — Google Ads segmentation only, it never
 * changes conversion counts.
 *
 * ⚠️ Derived from the RAW intent message, never from `q`: `q` deliberately
 * swaps a pasted URL for the scraped product title (see the note below), so a
 * URL test against it reports `url_prefix` only when the title FAILED to
 * resolve — inverted from intent, on the surface carrying most of the volume.
 *
 * Uses `isPromptUrl` rather than a local scheme test so this agrees with
 * `deriveChatResultPathway`, which classifies the same paste for PostHog. Two
 * rules would eventually disagree and report one search as a paste to one sink
 * and as text to the other.
 *
 * The image scan spans every message PASSED IN, not just the intent one — a
 * clarifying question means the newest message is a text answer to an image
 * search, and that must still read as `image`. Callers are responsible for
 * passing only the messages belonging to the search being labelled; the chat
 * caller slices from the previous product-bearing turn so an image attached to
 * search one cannot label search three.
 */
export function deriveChatSearchType(
  userMessages: PerformedDupeUserMessage[]
): DupeSearchType {
  if (isPromptUrl(findIntentText(userMessages))) return 'url_prefix'
  if (userMessages.some((m) => (m.imageUrls?.length ?? 0) > 0)) return 'image'
  return 'text'
}

export function buildPerformedDupeChatProps({
  userMessages,
  broadcastTitle,
  originalProduct,
  productCount,
  entrySource,
}: PerformedDupeChatInput): PerformedDupeChatProps {
  // What the user searched for.
  //
  // ⚠️ Not simply the last user message. When the brain asks a clarifying
  // question, the shopper's reply IS a user message — but it is "Under $2,000"
  // or "No preference", not what they are shopping for. The products land on
  // the turn AFTER that reply, so the naive read attributes the search, and
  // every purchase that follows it, to an answer fragment. That is worse than
  // useless in an email: it is what the subject line would say.
  //
  // So walk back past the clarification answers to the message that actually
  // carried the intent. If every user message is an answer — a product-page
  // chat the brain opened with a question — there is no query here, and the
  // scraped title (`broadcastTitle`) is the honest anchor.
  //
  // ⚠️ Not `broadcastTitle` FIRST either: that is frozen once, at the first
  // resolved title after mount, and the event fires on the first
  // PRODUCT-BEARING turn — not always the same turn. A deferred or resumed
  // thread produces products on a later message while `broadcastTitle` still
  // holds the mount-time query (or nothing). The event then looks perfectly
  // well-formed while attaching the products to the wrong text. A stale query
  // is worse than a missing one.
  //
  // A pasted URL is not a search term, so for that case fall through to the
  // scraped product title — the same choice v1 makes (`dupesFullData?.q ??
  // productTitle`).
  const intentText = findIntentText(userMessages)

  // For a pasted URL, the turn's own source product names it. `broadcastTitle`
  // alone is not enough: it is read from the streaming handler's closure and is
  // still empty on most turns, so about two-thirds of chat searches recorded the
  // raw link as `q`. The brain spells the title `name`, HTML-encoded.
  const sourceName =
    typeof originalProduct?.name === 'string'
      ? decodeHTML(originalProduct.name).trim()
      : ''

  const q =
    intentText && !isPromptUrl(intentText)
      ? intentText
      : sourceName || broadcastTitle?.trim() || intentText || null

  // Read through the shared derivation rather than a local coercion so the
  // anchor is parsed by the SAME rules as every dupe price on the click event
  // and as the grid's "% LESS" badge. Two parsers would eventually disagree on
  // a locale separator and quietly skew every "you saved X%" built on the pair.
  const anchor = derivePriceProps({ result: originalProduct })

  // Only the messages of THIS search: the intent message and the answers after
  // it. Earlier messages belong to an attempt that found nothing, and an image
  // attached there would mislabel a later text search. Same slice as the ads
  // search type in BrainChat, so the two report the same thing.
  const intentIndex = userMessages.reduce(
    (last, m, i) => (m.isClarificationAnswer ? last : i),
    -1
  )
  const searchMessages =
    intentIndex >= 0 ? userMessages.slice(intentIndex) : userMessages

  return {
    q: q || null,
    price_anchor: anchor?.price ?? null,
    price_anchor_currency: anchor?.price_currency ?? null,
    results_count: productCount,
    // The stream is done, so the count is final.
    results_phase: 'full',
    query_type: QUERY_TYPE[deriveChatSearchType(searchMessages)],
    search_source: entrySource,
    // ⚠️ `zero_results` is deliberately ABSENT, not `false`. The caller only
    // fires when `productCount > 0`, so a chat search that returned nothing
    // never emits the event at all — a zero cannot be observed from this
    // surface. Stamping `false` would claim we looked and found some, on
    // exactly the searches we never saw. Absent = not measured here.
    // Zero-result chat turns have their own event, `Search No Results`, built
    // from these same props; it must NOT be done by widening this event's fire
    // condition, which would change the volume of 70.8% of `Performed Dupe`
    // and break every rate built on it.
  }
}

export interface SearchNoResultsInput {
  /** A `search` tool call this turn returned `[]`. */
  searchedEmpty: boolean
  /** A `search` this turn found rows still on screen (re-derived on tier_reset). */
  searchFound: boolean
  /** The turn was submitted with a forced URL search (a pasted link alone). */
  forceUrlSearch: boolean
  userMessages: PerformedDupeUserMessage[]
  /** Products in the final grid for this turn. */
  productCount: number
  /** A Dupe Maxx guide was served, which is a result. */
  dupeMaxxServed: boolean
}

/**
 * Whether a chat turn should send `Search No Results`.
 *
 * Two shapes count. A `search` that returned `[]`, keyed on the turn's own
 * outcome rather than the final grid: `showProducts` can redisplay an earlier
 * grid after an empty search, and that search still found nothing. Or a pasted
 * link the server could not read: its forced URL search falls through
 * server-side without a `search` event reaching the client, so there the only
 * sign is a forced-URL first turn that ended with an empty grid. A link typed
 * with a question never forces a search, so it never counts.
 */
export function shouldTrackSearchNoResults({
  searchedEmpty,
  searchFound,
  forceUrlSearch,
  userMessages,
  productCount,
  dupeMaxxServed,
}: SearchNoResultsInput): boolean {
  if (searchFound || dupeMaxxServed) return false
  if (searchedEmpty) return true
  const isUnreadPastedLink =
    forceUrlSearch &&
    userMessages.length === 1 &&
    deriveChatSearchType(userMessages) === 'url_prefix'
  return isUnreadPastedLink && productCount === 0
}

/**
 * `query_type` for a "more like this" search, which runs on the product's
 * photo, or on its link when it has none.
 */
export function moreLikeThisQueryType(match: {
  image?: string | null
  thumbnail?: string | null
}): PerformedDupeQueryType {
  return QUERY_TYPE[match.image || match.thumbnail ? 'image' : 'url_prefix']
}
